import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    CircularProgress,
    IconButton,
    Slider,
    Stack,
    Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

const STATE = {
    IDLE: 'idle',
    CONNECTING: 'connecting',
    LISTENING: 'listening',
    DISCONNECTED: 'disconnected',
    ERROR: 'error',
};

const WHEP_PATH = '/videortc/';

/**
 * Component A — Listen to Horn.
 *
 * Starts a WHEP session to receive audio from the horn's microphone stream
 * (from_horn). Only available when Two Way is enabled on this horn.
 *
 * Uses explicit Play/Stop buttons — no automatic playback on mount.
 * Cleans up the PeerConnection on Stop, navigation or unmount.
 */
const HornListenComponent = ({ fromHornStream, onError, onStatusChange, embedded = false }) => {
    const [listenState, setListenState] = useState(STATE.IDLE);
    const [errorMsg, setErrorMsg] = useState('');
    const [volume, setVolume] = useState(80);
    const audioRef = useRef(null);
    const pcRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            cleanupWhep();
        };
    }, []);

    const cleanupWhep = useCallback(() => {
        if (pcRef.current) {
            pcRef.current.close();
            pcRef.current = null;
        }
        if (audioRef.current) {
            audioRef.current.srcObject = null;
        }
    }, []);

    const buildWhepUrl = useCallback(() => {
        return WHEP_PATH + encodeURIComponent(fromHornStream) + '/whep';
    }, [fromHornStream]);

    const startListening = useCallback(async () => {
        if (listenState === STATE.CONNECTING || listenState === STATE.LISTENING) return;
        if (!fromHornStream) {
            setErrorMsg('Horn stream not yet available.');
            setListenState(STATE.ERROR);
            return;
        }

        setListenState(STATE.CONNECTING);
        setErrorMsg('');

        try {
            const pc = new RTCPeerConnection({
                iceServers: [],
                bundlePolicy: 'max-bundle',
            });
            pcRef.current = pc;

            pc.ontrack = (event) => {
                if (!mountedRef.current) return;
                if (audioRef.current && event.streams[0]) {
                    audioRef.current.srcObject = event.streams[0];
                    audioRef.current.volume = volume / 100;
                    audioRef.current.play().catch(() => {});
                }
            };

            pc.oniceconnectionstatechange = () => {
                if (!mountedRef.current) return;
                if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
                    setListenState(STATE.DISCONNECTED);
                    setErrorMsg('Horn audio stream disconnected.');
                }
            };

            pc.addTransceiver('audio', { direction: 'recvonly' });

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            const whepUrl = buildWhepUrl();
            const response = await fetch(whepUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/sdp' },
                body: pc.localDescription.sdp,
                credentials: 'include',
            });

            if (!mountedRef.current) {
                cleanupWhep();
                return;
            }

            if (!response.ok) {
                throw new Error('WHEP response ' + response.status);
            }

            const answerSdp = await response.text();
            if (!mountedRef.current) {
                cleanupWhep();
                return;
            }

            await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

            if (!mountedRef.current) {
                cleanupWhep();
                return;
            }

            setListenState(STATE.LISTENING);
        } catch (error) {
            if (!mountedRef.current) return;
            cleanupWhep();
            const msg = error?.message || 'Failed to connect to horn audio.';
            setErrorMsg(msg);
            setListenState(STATE.ERROR);
            onError?.(msg);
        }
    }, [listenState, fromHornStream, volume, buildWhepUrl, cleanupWhep, onError]);

    const stopListening = useCallback(() => {
        cleanupWhep();
        setListenState(STATE.IDLE);
        setErrorMsg('');
    }, [cleanupWhep]);

    const handleVolumeChange = useCallback((_, newValue) => {
        setVolume(newValue);
        if (audioRef.current) {
            audioRef.current.volume = newValue / 100;
        }
    }, []);

    const stateLabel = {
        [STATE.IDLE]: 'Idle',
        [STATE.CONNECTING]: 'Connecting…',
        [STATE.LISTENING]: 'Listening',
        [STATE.DISCONNECTED]: 'Disconnected',
        [STATE.ERROR]: 'Error',
    }[listenState] ?? listenState;

    const isListening = listenState === STATE.LISTENING;
    const isConnecting = listenState === STATE.CONNECTING;

    const dismissError = useCallback(() => setErrorMsg(''), []);
    useEffect(() => {
        onStatusChange?.({ error: errorMsg, dismissError });
    }, [onStatusChange, errorMsg, dismissError]);
    useEffect(() => () => onStatusChange?.(null), [onStatusChange]);

    return (
        <Box sx={embedded ? { py: 0.5 } : { p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
                {embedded ? 'Listen' : '🔊 Listen to Horn'}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                {!isListening && !isConnecting && (
                    <IconButton
                        color="primary"
                        onClick={startListening}
                        disabled={!fromHornStream}
                        size="small"
                        title="Start listening"
                    >
                        <PlayArrowIcon />
                    </IconButton>
                )}
                {(isListening || isConnecting) && (
                    <IconButton
                        color="error"
                        onClick={stopListening}
                        size="small"
                        title="Stop listening"
                    >
                        <StopIcon />
                    </IconButton>
                )}
                {isConnecting && <CircularProgress size={16} />}
                <Typography variant="body2" color={isListening ? 'success.main' : 'text.secondary'}>
                    {stateLabel}
                </Typography>

                {isListening && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1, minWidth: 120 }}>
                        <VolumeUpIcon fontSize="small" />
                        <Slider
                            size="small"
                            value={volume}
                            onChange={handleVolumeChange}
                            min={0}
                            max={100}
                            aria-label="Volume"
                            sx={{ width: 100 }}
                        />
                    </Stack>
                )}
            </Stack>

            {!embedded && errorMsg && (
                <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setErrorMsg('')}>
                    {errorMsg}
                </Alert>
            )}

            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio ref={audioRef} style={{ display: 'none' }} />
        </Box>
    );
};

HornListenComponent.propTypes = {
    fromHornStream: PropTypes.string.isRequired,
    onError: PropTypes.func,
    onStatusChange: PropTypes.func,
    embedded: PropTypes.bool,
};

HornListenComponent.defaultProps = {
    fromHornStream: '',
    onError: null,
};

export default HornListenComponent;
